import React from 'react';
import styles from './LaboratoryRequestCreateFormItem.module.scss';
import { LaboratoryRequestItem } from '../../Interfaces/LaboratoryRequestItem.intefrace';
import { LaboratoryRequestItemType } from '../../Enums/LaboratoryRequestItemType';
import LaboratoryRequestCreateFormItemChildren from './LaboratoryRequestCreateFormItemChildren';
import LaboratoryRequestCreateFormItemTabContainer from './LaboratoryRequestCreateFormItemTabContainer';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import InputField from '../../../../Components/Form/components/InputField';
import useLaboratoryRequestStyles from '../../Hooks/useLaboratoryRequestStyles';
import RadioListField from '../../../../Components/Form/components/RadioListField';
import TextAreaField from '../../../../Components/Form/components/TextAreaField';
import useCustomInputs from '../../../Base/Hooks/useCustomInputs';
import FormCondition from '../../../../Components/Form/FormCondition';
import LaboratoryRequestMaterials from './LaboratoryRequestMaterials';

interface Props {
	item: LaboratoryRequestItem;
	onItemSelect: (item: LaboratoryRequestItem) => void;
}

const LaboratoryRequestCreateFormItem: React.FC<Props> = ({
	item,
	onItemSelect,
}) => {
	const style = useLaboratoryRequestStyles(item);
	const { prepareValueForGroupCheckbox } = useCustomInputs();
	const handleChangeCheckbox = () => {
		onItemSelect(item);
	};

	let component = <></>;

	switch (item.type) {
		case LaboratoryRequestItemType.GROUP:
			component = (
				<div className={styles.group}>
					<div
						className={styles.title}
						style={{ borderColor: item.settings?.backgroundColor }}>
						{item.title}
					</div>
					<LaboratoryRequestCreateFormItemChildren
						items={item.children}
						onItemSelect={onItemSelect}
					/>
				</div>
			);
			break;
		case LaboratoryRequestItemType.CONTAINER:
			component = (
				<div className={styles.container} style={style}>
					<LaboratoryRequestCreateFormItemChildren
						items={item.children}
						onItemSelect={onItemSelect}
					/>
				</div>
			);
			break;
		case LaboratoryRequestItemType.TAB_CONTAINER:
			component = (
				<LaboratoryRequestCreateFormItemTabContainer
					items={item.children}
					onItemSelect={onItemSelect}
				/>
			);
			break;
		case LaboratoryRequestItemType.TITLE:
			component = (
				<div className={styles.title} style={style}>
					{item.value}
				</div>
			);
			break;
		case LaboratoryRequestItemType.SUB_TITLE:
			component = (
				<div className={styles.subtitle} style={style}>
					{item.value}
				</div>
			);
			break;
		case LaboratoryRequestItemType.GROUP_CHECKBOX:
			if (item.value && typeof item.value === 'string') {
				//API IS SENDING KEYS AS ONE STRING IN FORMAT {'32234','44322','32434'} SO WE NEED TO CALL THIS FUNCTION TO GET ARRAY OF KEYS
				const val = prepareValueForGroupCheckbox(item.value);
				component = (
					<CheckboxField
						key={item.id}
						tx={item.title}
						name={val.join(';')}
						className={styles.checkbox}
						customOnChange={handleChangeCheckbox}
						description={item.description}
						beforeLabelElement={
							<LaboratoryRequestMaterials
								materials={item?.settings?.materials || []}
							/>
						}
					/>
				);
			}
			break;
		case LaboratoryRequestItemType.CHECKBOX:
			if (item.value && typeof item.value === 'string') {
				component = (
					<>
						<CheckboxField
							key={item.id}
							tx={item.title}
							name={item.value}
							className={styles.checkbox}
							customOnChange={handleChangeCheckbox}
							description={item.description}
							beforeLabelElement={
								<LaboratoryRequestMaterials
									materials={item?.settings?.materials || []}
								/>
							}
						/>
					</>
				);
			}
			break;
		case LaboratoryRequestItemType.TEXT_FIELD:
			if (item.value && typeof item.value === 'string') {
				component = (
					<InputField
						key={item.id}
						name={item.value}
						label={item.title}
						className={styles.textfield}
						hideRequired
					/>
				);
			}
			break;
		case LaboratoryRequestItemType.TEXT_AREA:
			if (item.value && typeof item.value === 'string') {
				component = (
					<TextAreaField
						key={item.id}
						name={item.value}
						label={item.title}
						className={styles.textfield}
						hideRequired
					/>
				);
			}
			break;
		case LaboratoryRequestItemType.SPACE:
			component = <div className={styles.space} style={style} />;
			break;
		case LaboratoryRequestItemType.RADIO_LIST:
			if (item.value && typeof item.value === 'string' && item.options) {
				component = (
					<RadioListField
						key={item.id}
						name={item.value}
						label={item.title}
						data={item.options}
						className={styles.checkbox}
						inLine
						hideRequired
					/>
				);
			}
			break;
		case LaboratoryRequestItemType.CONDITION:
			if (item.value && typeof item.value === 'string') {
				component = (
					<FormCondition
						controlName={item.value}
						equal={item?.settings?.equal}
						rules={item?.settings?.rules}>
						<LaboratoryRequestCreateFormItemChildren
							items={item.children}
							onItemSelect={onItemSelect}
						/>
					</FormCondition>
				);
			}
			break;
	}

	return component;
};

export default LaboratoryRequestCreateFormItem;
